import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { deleteWidgetApi, editWidgetApi, reorderReportWidgets } from "api/report";

import getLogger from "utils/logger.util";
import { isAllwidgetEnable, isAnywidgetEnable, isCurrentLayoutEmpty } from "utils/reportUtils";
import { sortWidgetsBasedOnOrder } from "utils/utils";
import { DeleteWidgetStrings, valueStrings } from "constants/constantStrings";

import DupontDialog from "components/DupontDialog";
import EditWidgetModal from "components/EditWidgetModal";
import GridLayoutDragDrop from "components/GridLayout";
import GRID_CONSTANTS from "components/GridLayout/GridConstants";

import FileDownloadHandler from "../Chart/ChartDownloadData/dataDownloadHandler";

import ReportGenerate from "./ReportGenerate";
import ReportListItem from "./ReportListItem";

const ReportList = ({
  widgets,
  setWidgets,
  systemID,
  page,
  tagsList,
  currentLayout,
  editReportWidget,
  isWidgetEditing,
  setWidgetAdded,
  widgetAdded,
  setIsUpdating,
  isUpdating,
  isReportPanelOpened,
  baselineList,
  setIsAddWidgetFormOpen
}) => {
  const objAppInsights = useAppInsightsHandler();
  const tagsAsObj = useSelector(state => state.tags.tagsAsObj);
  const getLayouts = widgets =>
    widgets.map(widget => ({
      i: widget.Id.toString(),
      x: 0,
      y: widgets.indexOf(widget),
      w: GRID_CONSTANTS.DEFAULT_GRID_WIDTH_REPORT,
      h: GRID_CONSTANTS.DEFAULT_GRID_HEIGHT,
      id: widget.Id
    }));
  const [editWidgetData, setEditWidgetData] = useState(null);
  const [editWidgetId, setEditWidgetId] = useState(null);
  const [deleteWidgetId, setDeleteWidgetId] = useState(null);
  const [isWidgetDeleteDlgOpen, setIsWidgetDeleteDlgOpen] = useState(false);
  const [isReportGenerateEnable, setReportGenerateEnable] = useState(isAnywidgetEnable(widgets));
  const [isToUpdateReportPreview, setUpdateReportPreview] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isIndeterminate, setIndeterminate] = useState(false);
  const [layouts, setLayouts] = useState(getLayouts(widgets));
  const Logger = getLogger("ReportList");

  useEffect(() => {
    if (widgets) {
      setLayouts(getLayouts(widgets));
      setReportGenerateEnable(isAnywidgetEnable(widgets));
      if (isCurrentLayoutEmpty(currentLayout)) {
        setReportGenerateEnable(false);
      }
      setIsChecked(isAllwidgetEnable(widgets));
      setIndeterminate(!isAllwidgetEnable(widgets) && isAnywidgetEnable(widgets));
    }
  }, [widgets, currentLayout]);

  useEffect(() => {
    if (!isWidgetEditing) setEditWidgetData(null);
  }, [isWidgetEditing]);

  const scrollWidgetsToBottom = () => {
    const element = document.querySelector(".report_list_container");
    element.scrollTop = element.scrollHeight;
  };

  useEffect(() => {
    if (widgetAdded) {
      const widgetData = { ...widgetAdded, isToUpdatePreview: true, response: null, url: null };
      const newList = [...widgets, widgetData];
      setWidgets(newList);
      setWidgetAdded(null);
      setTimeout(() => scrollWidgetsToBottom());
    }
  }, [widgetAdded]);

  useEffect(() => {
    if (widgets) {
      let updatedWidgets;
      if (isToUpdateReportPreview) {
        updatedWidgets = widgets.map(widget => ({ ...widget, isToUpdatePreview: true, response: null, url: null, error: null }));
      } else {
        updatedWidgets = widgets.map(widget => ({ ...widget, isToUpdatePreview: false }));
      }

      setWidgets(updatedWidgets);
      setUpdateReportPreview(false);
    }
  }, [isToUpdateReportPreview]);

  const onWidgetEnable = (isChecked, report) => {
    const editSwitchObject = {
      Id: report.Id,
      Enabled: isChecked,
      LayoutID: currentLayout[systemID][page].ID,
      Attributes: {} // sending blank to be handled by Backend team
    };

    const reportWidgetSwitch = async () => {
      try {
        const response = await editWidgetApi(editSwitchObject);
        if (response) {
          const editWdigetIdx = getItemIdxId(response.Id);
          widgets[editWdigetIdx].Enabled = response.Enabled;
          setIsChecked(isAllwidgetEnable(widgets));
          setIndeterminate(!isAllwidgetEnable(widgets) && isAnywidgetEnable(widgets));
          setWidgets(widgets);
          setReportGenerateEnable(isAnywidgetEnable(widgets));
        }
      } catch (err) {
        Logger.error("reportWidgetSwitch error: ", err);
      }
    };
    reportWidgetSwitch();
  };

  const getItemIdxById = widgetId => widgets.findIndex(item => item.Id === widgetId);

  const downloadReportCSV = widgetId => {
    widgets.map(item => {
      if (item.Id === widgetId) {
        const tagIds = Object.values(item?.Attributes?.SystemTag || {}).filter(id => id && id !== "");
        const traceNameToDesc = tagIds.reduce((map, tagId) => {
          const tag = tagsAsObj[tagId];
          if (tag) map[`${tag.TagName} (${tag.Units})`] = tag.Description;
          return map;
        }, {});
        const predictionTagDesc = item.Type === "Prediction" ? tagsAsObj[tagIds[0]]?.Description : null;
        const descriptionRow =
          item?.chartData?.reduce(
            (acc, trace) => {
              const desc = traceNameToDesc[trace.name] ?? predictionTagDesc;
              acc[trace.name] = desc ? `(${desc})` : "(description - N/A)";
              return acc;
            },
            { TimeStamp: "" }
          ) || null;
        FileDownloadHandler.downloadWidgetCSVInReports(item, descriptionRow);
      }
    });
  };

  const onWidgetAction = (actionType, widgetId) => {
    const itemIdx = getItemIdxById(widgetId);
    switch (actionType) {
      case "EDIT_WIDGET":
        onEditReportWidget(itemIdx);
        break;
      case "DELETE_WIDGET":
        deleteReportWidget(widgetId);
        break;
      case "DOWNLOAD_WIDGET_CSV":
        downloadReportCSV(widgetId);
        break;
      // ToDo Needs to implement these..
      case "MOVE_WIDGET_UP":
        break;
      case "MOVE_WIDGET_DOWN":
        break;
    }
  };

  const deleteReportWidget = widgetId => {
    setIsWidgetDeleteDlgOpen(true);
    setDeleteWidgetId(widgetId);
  };

  const onDeleteDlgClose = async data => {
    objAppInsights.trackEvent("deleteWidgetInReports");
    if (data === "CONFIRM") {
      try {
        setIsUpdating(true);
        await deleteWidgetApi(currentLayout[systemID][page].ID, deleteWidgetId);
        const filteredWidgets = widgets.filter(widget => widget.Id !== deleteWidgetId);
        setWidgets(filteredWidgets);
        if (deleteWidgetId === editWidgetId) {
          onEditWidgetClose(false);
        }
      } catch (error) {
        Logger.error("onDeleteDlgClose - error: ", error);
      } finally {
        setIsUpdating(false);
      }
      setDeleteWidgetId(null);
    }
    setIsWidgetDeleteDlgOpen(false);
  };

  const onEditReportWidget = index => {
    setIsAddWidgetFormOpen(false);
    setEditWidgetId(widgets[index].Id);
    const data = { ...widgets[index], tagsList, isEdit: true, page };
    setEditWidgetData({ ...data, currentLayout: currentLayout[systemID][page] });
    editReportWidget(true);
  };

  const onEditWidgetClose = data => {
    if (data && typeof data === "object") editWidget(data);
    setEditWidgetData(null);
    editReportWidget(false);
  };

  const getItemIdxId = id => widgets.findIndex(item => item.Id === id);

  const editWidget = async payload => {
    objAppInsights.trackEvent("updateWidgetInReports");
    const editWdigetIdx = getItemIdxId(editWidgetId);
    widgets[editWdigetIdx].isUpdating = true;
    widgets[editWdigetIdx].error = false;
    setWidgets(widgets);
    try {
      const resp = await editWidgetApi(payload);
      if (resp) {
        const updatedWidgetData = resp;
        updatedWidgetData.isToUpdatePreview = true;
        updatedWidgetData.isUpdating = false;
        widgets[editWdigetIdx] = updatedWidgetData;
      }
    } catch (err) {
      widgets[editWdigetIdx].isUpdating = false;
    } finally {
      setWidgets([...widgets]);
    }
  };

  const getDeleteWidgetType = () => {
    const deleteWdigetIdx = getItemIdxId(deleteWidgetId);
    const itemToDelete = widgets[deleteWdigetIdx];
    return itemToDelete?.Header || itemToDelete?.WidgetType;
  };

  const getDeleteDlgTitle = () => {
    const name = getDeleteWidgetType();
    return DeleteWidgetStrings.title.replace("%", name);
  };

  const getDeleteDlgDesc = () => {
    const name = currentLayout[systemID][page].Name;
    return DeleteWidgetStrings.description.replace("%", name);
  };

  const onDragEndNew = layout => {
    setLayouts(layout);
    const jsonData = layout.reduce((acc, item) => {
      const columnName = parseInt(item.i);
      acc[columnName] = item.y;
      return acc;
    }, {});
    const payload = { Order: jsonData, LayoutID: currentLayout[systemID][page].ID };
    const reorder = async () => {
      try {
        const response = await reorderReportWidgets(payload);

        if (response) {
          const updatedWidgets = widgets.map(widget => ({
            ...widget,
            ...response.find(responseWidget => responseWidget.Id === widget.Id)
          }));
          setWidgets(sortWidgetsBasedOnOrder(updatedWidgets));
        }
      } catch (err) {
        Logger.error(err);
      }
    };
    reorder();
  };

  const widgetID = item => `report_${item.Type || ""}_${item?.Attributes?.SystemTag?.[0] || "widget"}`.toLowerCase();

  return (
    <>
      <div>
        <ReportGenerate
          systemID={systemID}
          page={page}
          currentLayout={currentLayout}
          isReportGenerateEnable={isReportGenerateEnable}
          setUpdateReportPreview={setUpdateReportPreview}
          reports={widgets}
          setWidgets={setWidgets}
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
        />
        {!isCurrentLayoutEmpty(currentLayout) && (
          <div className="report_list_container">
            <GridLayoutDragDrop
              layouts={layouts}
              rowHeight={GRID_CONSTANTS.MAX_ROW_HEIGHT_REPORT}
              onDragEnd={onDragEndNew}
              noOfItemPerCol={GRID_CONSTANTS.MAX_GRID_COLS_REPORT}
              isDashboardPanelOpened={isReportPanelOpened}
            >
              {widgets &&
                widgets.map(item => (
                  <div id={widgetID(item)} key={item.Id.toString()}>
                    <ReportListItem
                      item={item}
                      baselineList={baselineList}
                      setUpdateReportPreview={setUpdateReportPreview}
                      onWidgetAction={onWidgetAction}
                      onWidgetEnable={onWidgetEnable}
                      isToUpdateReportPreview={isToUpdateReportPreview}
                      error={item.error}
                      LayoutId={currentLayout[systemID][page].ID}
                    />
                  </div>
                ))}
            </GridLayoutDragDrop>
          </div>
        )}
      </div>
      {isWidgetDeleteDlgOpen && (
        <DupontDialog
          open={isWidgetDeleteDlgOpen}
          type="primary"
          title={getDeleteDlgTitle()}
          cancelBtnText={valueStrings.no}
          confirmBtnText={valueStrings.yes}
          onClose={onDeleteDlgClose}
          isOverlayLoader={isUpdating}
        >
          {getDeleteDlgDesc()}
        </DupontDialog>
      )}
      {editWidgetData && <EditWidgetModal data={editWidgetData} onEditWidgetClose={onEditWidgetClose} />}
    </>
  );
};

export default ReportList;
